// src/components/Projects/Projects.jsx
import React, { useState, useEffect, useMemo } from "react";
import ProjectsTable from "./ProjectsTable";
import { projectProfileConfig } from "../../shared/profile-configs/ProjectProfileConfig";
import EntityProfile from "../../shared/components/EntityProfile";
import DeleteConfirmDialog from "../../shared/components/DeleteConfirmDialog";
import BrandPagination from "../../layouts/Pagination";

import {
  Box,
  Button,
  Card,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import { useNavigate, useSearchParams } from "react-router-dom";
import useRowSelection from "../../hooks/useRowSelection";
import useProjectsData from "./hooks/useProjectsData";

import { useAuth } from "../../providers/AuthProvider";
import api from "../../services/api/axios";
import SearchBar from "../../shared/components/SearchBar";
import {
  ACTION_MENU_ICON_SX,
  ACTION_MENU_ICON_TONES,
  ACTION_MENU_PAPER_SX,
  ACTIONS_HEADER_BTN_SX,
} from "../../theme/buttonstyles";

const BRAND_BLUE = "#00336C";
const PAGE_BG = "#f7f9fc";

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  background: "#ffffff",
  overflow: "hidden",
};

// Simplified phases: only two allowed values
const PHASES = [
  "Project Created",
  "Planning",
  "In Progress",
  "Project Completed",
];

function Projects() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchParams] = useSearchParams();

  // Filters + search
  const [searchTerm, setSearchTerm] = useState("");
  const [projectTypeFilter, setProjectTypeFilter] = useState("");
  const [projectPhaseFilter, setProjectPhaseFilter] = useState("");
  const [projectManagerFilter, setProjectManagerFilter] = useState("");

  useEffect(() => {
    const phaseFromUrl = searchParams.get("phase");

    if (phaseFromUrl && PHASES.includes(phaseFromUrl)) {
      setProjectPhaseFilter(phaseFromUrl);
      setPage(1);
    }
  }, [searchParams]);

  // Managers list
  const [managerOptions, setManagerOptions] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);

  // Server pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const { token } = useAuth();
  const navigate = useNavigate();

  const handleClose = () => {
    setProfileOpen(false);
    setSelectedProject(null);
  };

  const mapTypeForApi = (val) => {
    if (!val) return undefined;

    if (val === "business_attraction") return "Attraction";
    if (val === "business_retention_expansion") return "Expansion";
    if (val === "startup") return "Startup";

    return undefined;
  };

  const {
    projects,
    totalCount,
    setProjects,
    refetch: fetchProjects,
  } = useProjectsData({
    token,
    page,
    pageSize,
    searchTerm,
    projectTypeFilter: mapTypeForApi(projectTypeFilter),
    projectPhaseFilter,
    projectManagerFilter,
    onUnauthorized: () => alert("Session expired. Please log in again."),
  });

  // Selection (shared hook)
  const {
    selectedIds,
    selectedCount,
    isSelected,
    toggleOne,
    toggleMany,
    clearSelection,
  } = useRowSelection();

  const projectProfileSections = useMemo(() => {
    if (!selectedProject) return [];
    return projectProfileConfig(selectedProject);
  }, [selectedProject]);

  // ---------------------------
  // Derived selection state (page-aware)
  // ---------------------------
  const visibleIds = useMemo(
    () => projects.filter((p) => p?.id != null).map((p) => String(p.id)),
    [projects],
  );

  const allCurrentSelected =
    visibleIds.length > 0 && visibleIds.every(isSelected);

  const someCurrentSelected = visibleIds.some(isSelected);

  const handleTogglePage = () => toggleMany(visibleIds);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const actionsMenuOpen = Boolean(actionsAnchorEl);

  useEffect(() => {
    const handler = (e) => {
      const { projectId, newPhase } = e.detail || {};
      if (!projectId || !newPhase) return;

      // Optimistic local update only
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId ? { ...p, project_phase: newPhase } : p,
        ),
      );
    };

    window.addEventListener("projectPhaseChanged", handler);
    return () => window.removeEventListener("projectPhaseChanged", handler);
  }, [setProjects]);

  const fetchManagers = () => {
    if (!token) return;
    setManagersLoading(true);

    (async () => {
      try {
        const res = await api.get("/projects/managers", {
          params: {
            project_type: projectTypeFilter || undefined,
            project_phase: projectPhaseFilter || undefined,
            search: searchTerm || undefined,
          },
        });

        const list = Array.isArray(res.data) ? res.data.filter(Boolean) : [];
        setManagerOptions(list);

        if (projectManagerFilter && !list.includes(projectManagerFilter)) {
          setProjectManagerFilter("");
        }
      } catch (err) {
      } finally {
        setManagersLoading(false);
      }
    })();
  };

  useEffect(() => {
    fetchManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, projectTypeFilter, projectPhaseFilter, searchTerm]);

  useEffect(() => {
    clearSelection();
  }, [projects, clearSelection]);

  const handleExport = async () => {
    try {
      const resp = await api.get("/projects/export", {
        responseType: "blob",
        params: {
          project_manager: projectManagerFilter || undefined,
          project_type: projectTypeFilter || undefined,
          project_phase: projectPhaseFilter || undefined,
          search: searchTerm || undefined,
        },
      });

      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Projects_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Failed to export projects.");
    }
  };

  const renderCityState = (project) => {
    const parts = [project.city, project.state].filter(Boolean);
    return parts.length ? parts.join(", ") : "- - -";
  };

  // Reset to page 1 when filters/search change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, projectTypeFilter, projectPhaseFilter, projectManagerFilter]);

  const doDeleteProjects = async (ids) => {
    setDeleting(true);
    const results = await Promise.allSettled(
      ids.map((id) => api.delete(`/projects/${id}`)),
    );
    const succeeded = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - succeeded;

    if (succeeded > 0) {
      setSnackbarSeverity("success");
      setSnackbarMsg(
        `Deleted ${succeeded} project${
          succeeded === 1 ? "" : "s"
        } successfully.`,
      );
    }
    if (failed > 0) {
      setSnackbarSeverity("warning");
      setSnackbarMsg(`Deleted ${succeeded}, but ${failed} failed.`);
    }
    setSnackbarOpen(true);
    setDeleting(false);
    setDeleteConfirmOpen(false);

    fetchProjects();
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) {
      setSnackbarSeverity("info");
      setSnackbarMsg("No rows selected.");
      setSnackbarOpen(true);
      return;
    }
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    await doDeleteProjects(ids);
  };

  const handleOpenActionsMenu = (event) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleCloseActionsMenu = () => {
    setActionsAnchorEl(null);
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: PAGE_BG,
        pb: 4,
      }}
    >
      {/* ===== Sticky Header ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexWrap: "wrap",
          }}
        >
          {/* Left: icon + title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: "#648315",
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <WorkHistoryIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box
              sx={{
                fontSize: 21,
                fontWeight: 800,
                color: BRAND_BLUE,
                lineHeight: 1.05,
                whiteSpace: "nowrap",
              }}
            >
              Projects
            </Box>
          </Box>

          {/* Center: search */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search Project Name, Type, Company, or Location..."
              maxWidth={1700}
            />
          </Box>

          {/* Right: actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleOpenActionsMenu}
              sx={ACTIONS_HEADER_BTN_SX}
            >
              Actions
            </Button>

            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsMenuOpen}
              onClose={handleCloseActionsMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
            >
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  navigate("/projects/new");
                }}
              >
                <ListItemIcon
                  sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.add }}
                >
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Project</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  handleExport();
                }}
                disabled={projects.length === 0}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.export,
                  }}
                >
                  <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  clearSelection();
                }}
                disabled={selectedCount === 0}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.clear,
                  }}
                >
                  <ClearAllIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Clear Selection</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={async () => {
                  handleCloseActionsMenu();
                  await handleDeleteSelected();
                }}
                disabled={selectedCount === 0}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.delete,
                  }}
                >
                  <DeleteForeverIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{`Delete Selected (${selectedCount})`}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* ===== Content Area ===== */}
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        <Card sx={CARD_SX}>
          <ProjectsTable
            projects={projects}
            visibleIds={visibleIds}
            allCurrentSelected={allCurrentSelected}
            someCurrentSelected={someCurrentSelected}
            onTogglePage={handleTogglePage}
            isSelected={isSelected}
            toggleOne={toggleOne}
            navigate={navigate}
            renderCityState={renderCityState}
            PHASES={PHASES}
            setProjects={setProjects}
            api={api}
            projectTypeFilter={projectTypeFilter}
            onProjectTypeFilterChange={setProjectTypeFilter}
            projectPhaseFilter={projectPhaseFilter}
            onProjectPhaseFilterChange={setProjectPhaseFilter}
            projectManagerFilter={projectManagerFilter}
            onProjectManagerFilterChange={setProjectManagerFilter}
            managerOptions={managerOptions}
            managersLoading={managersLoading}
            setSelectedProject={setSelectedProject}
            setProfileOpen={setProfileOpen}
          />
        </Card>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <BrandPagination
            page={page}
            count={Math.ceil(totalCount / pageSize) || 1}
            onChange={(_e, value) => setPage(value)}
          />
        </Box>
      </Box>

      <EntityProfile
        open={profileOpen}
        entity={selectedProject}
        sections={projectProfileSections}
        dialogPaperSx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "#00336C",
          border: "1px solid #00336C",
        }}
        onClose={handleClose}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteSelected}
        loading={deleting}
        title="Delete Projects"
        description={`Are you sure you want to delete ${selectedIds.size} project${selectedIds.size === 1 ? "" : "s"}? This action can't be undone.`}
      />
    </Box>
  );
}

export default Projects;
